import { Pipe, PipeTransform } from '@angular/core';

/**
 * Orders an array of objects or primitive values by a specified attribute or maps them using a provided mapping object.
 * @param value The array to be ordered.
 * @param order The order direction, either 'desc' or 'asc'. Default is 'asc'.
 * @param attribute The attribute of the objects to sort by (ONLY SORT, No mapping). ('xx.yy' for nested attributes)
 * @param mapTo An optional mapping object to map the values before sorting. (Provided attribute will be used as key)
 * @returns The ordered array.
 */
@Pipe({
  name: 'orderBy',
  standalone: true
})
export class OrderByPipe implements PipeTransform {
  /**
   * Orders an array of objects or primitive values by a specified attribute or maps them using a provided mapping object.
   * @param value The array to be ordered.
   * @param order The order direction, either 'desc' or 'asc'. Default is 'asc'.
   * @param attribute The attribute of the objects to sort by (ONLY SORT, No mapping). ('xx.yy' for nested attributes)
   * @param mapTo An optional mapping object to map the values before sorting. (Provided attribute will be used as key)
   * @returns The ordered array.
   */
  transform<T = any>(
    value: T[] | null | undefined,
    order: 'desc' | 'asc' = 'asc',
    attribute?: string,
    mapTo?: Record<string | number, T>
  ): T[] {
    // Return empty array if value is null/undefined or not an array
    if (value == null || !Array.isArray(value)) {
      return value ?? [];
    }
    // Map values if mapTo is provided
    let mappedArray: T[] = value;
    if (mapTo) {
      // Extract attribute values if attribute is provided
      let attributedArray: T[] = value;
      if (attribute) {
        attributedArray = value.map((item) => {
          return this.getNestedAttribute(item, attribute);
        });
      }
      if (
        attributedArray.some((item) => {
          return typeof item !== 'string' || typeof item !== 'number';
        })
      ) {
        console.warn('OrderByPipe: When using mapTo, the array items must be of type string or number to match the keys of the map.');
        return value;
      }
      mappedArray = attributedArray.map((item) => mapTo[item as string | number]).filter((item) => item !== undefined);
    }
    // Sort the final array
    return mappedArray.sort((a, b) => {
      if (attribute && !mapTo) {
        a = this.getNestedAttribute(a, attribute);
        b = this.getNestedAttribute(b, attribute);
      }
      return this.sortValues(a, b, order);
    });
  }

  /**
   * Helper method to sort two values based on the specified order.
   * @param a value a
   * @param b value b
   * @param order 'desc' | 'asc'
   * @returns A number indicating the sort order.
   */
  private sortValues<T>(a: T, b: T, order: 'desc' | 'asc'): number {
    if (typeof a === 'number' && typeof b === 'number') {
      return order === 'asc' ? a - b : b - a;
    } else if (a instanceof Date && b instanceof Date) {
      return order === 'asc' ? a.getTime() - b.getTime() : b.getTime() - a.getTime();
    } else if (typeof a === 'string' && typeof b === 'string') {
      const comparison = a.localeCompare(b);
      return order === 'asc' ? comparison : -comparison;
    }
    const aStr = String(a).toLowerCase();
    const bStr = String(b).toLowerCase();
    const comparison = aStr.localeCompare(bStr);
    if (comparison !== 0) {
      return order === 'asc' ? comparison : -comparison;
    }
    return 0;
  }

  /**
   * Helper method to get nested attribute value from an object.
   * @param obj The object to extract the attribute from.
   * @param attributePath The path to the attribute (e.g., 'xx.yy').
   * @returns The value of the nested attribute.
   */
  private getNestedAttribute(obj: any, attributePath: string): any {
    return attributePath.split('.').reduce((acc, part) => acc?.[part], obj);
  }
}
